import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Paper,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import axios from "axios";
import { toast } from "react-toastify";
import PastVisits from "../doctor-console/PastVisits/PastVisits";
import { displayPatientNameFromParts } from "../../utils/displayPatientName";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

export default function PatientLocalData() {
  const [authToken] = useState(
    sessionStorage.getItem("accessToken") || ""
  );
  const headers = { Authorization: `Bearer ${authToken}` };

  const [isAbdmEnabled] = useState(
    JSON.parse(sessionStorage.getItem("isAbdmEnabled") || "false")
  );

  const [patientList, setPatientList] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [careContextList, setCareContextList] = useState([]);
  const [s3BucketFhir, setS3BucketFhir] = useState(null);
  const [selectedContextForRetrieveFhir, setSelectedContextForRetrieveFhir] =
    useState([]);

  useEffect(() => {
    fetchPatientList();
  }, []);

  const fetchPatientList = () => {
    setLoadingPatients(true);
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}get_patient_list_with_aabha_info/`, {
        headers,
      })
      .then((res) => {
        setPatientList(res.data?.data || []);
      })
      .catch((err) => {
        console.error("Failed to fetch patients:", err);
        toast.error("Failed to load patient list");
      })
      .finally(() => setLoadingPatients(false));
  };

  const fetchCareContexts = (patient) => {
    const postData = {
      filter_details: {},
    };

    if (isAbdmEnabled && patient.abha_id) {
      postData.filter_type = "patient_abha";
      postData.filter_details.patient_abha = patient.abha_id;
    } else {
      postData.filter_type = "patient_id";
      postData.filter_details.patient_id = patient.patient_id;
    }

    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}fetch_care_contexts_by_filter/`,
        JSON.stringify(postData),
        { headers }
      )
      .then((res) => {
        if (res.data.status !== "Failed") {
          setCareContextList(res.data.care_contenxts || []);
        }
      })
      .catch((err) => console.error("Care context fetch error:", err));
  };

  const handlePatientSelect = (event, patient) => {
    if (!patient) {
      setSelectedPatient(null);
      setCareContextList([]);
      setS3BucketFhir(null);
      return;
    }
    setSelectedPatient(patient);
    setS3BucketFhir(null);
    setSelectedContextForRetrieveFhir([]);
    fetchCareContexts(patient);
  };

  const handleFhirArray = (fhirArray) => {
    setS3BucketFhir(fhirArray);
  };

  const getPatientLabel = (patient) => {
    const name = displayPatientNameFromParts(patient, "Unknown");
    const id = patient.patient_id || "";
    const phone = patient.phone_no || "";
    return `${name} | ${id}${phone ? ` | ${phone}` : ""}`;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1100, margin: "auto" }}>
      {/* Search Section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          background: "linear-gradient(135deg, #f5f7fa 0%, #e4ecf7 100%)",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: "#125386",
            fontWeight: 700,
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <SearchIcon sx={{ fontSize: 28 }} />
          Patient Historical Records
        </Typography>
        <Autocomplete
          options={patientList}
          loading={loadingPatients}
          getOptionLabel={getPatientLabel}
          onChange={handlePatientSelect}
          filterOptions={(options, { inputValue }) => {
            const search = inputValue.toLowerCase();
            return options.filter((opt) => {
              const name = displayPatientNameFromParts(opt, "").toLowerCase();
              const id = (opt.patient_id || "").toLowerCase();
              const phone = (opt.phone_no || "").toLowerCase();
              return (
                name.includes(search) ||
                id.includes(search) ||
                phone.includes(search)
              );
            });
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.patient_id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: "#125386", width: 36, height: 36 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    {displayPatientNameFromParts(option, "Unknown")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#666" }}>
                    ID: {option.patient_id}
                    {option.phone_no ? ` | Phone: ${option.phone_no}` : ""}
                  </Typography>
                </Box>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search by patient name, ID, or phone number..."
              variant="outlined"
              sx={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPatients && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="No patients found"
          sx={{ width: "100%" }}
        />
      </Paper>

      {/* Local Records Content */}
      {selectedPatient && (
        <Paper
          elevation={2}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Patient Info Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              pt: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#125386", fontWeight: 600 }}
            >
              {displayPatientNameFromParts(selectedPatient, "Patient")} — Local
              Records
            </Typography>
            <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
              ID: {selectedPatient.patient_id}
            </Typography>
          </Box>

          <Box sx={{ borderBottom: "1px solid #C7C7CC", mt: 1, mx: 1 }} />

          {/* PastVisits (Local data) */}
          <Box sx={{ p: 2 }}>
            <PastVisits
              patientData={selectedPatient}
              fhirArrayData={s3BucketFhir}
              careContextList={careContextList}
              selectedCareContext={selectedContextForRetrieveFhir}
              onSubmitFhirArray={handleFhirArray}
            />
          </Box>
        </Paper>
      )}

      {/* Empty state */}
      {!selectedPatient && (
        <Paper
          elevation={1}
          sx={{
            p: 6,
            borderRadius: "16px",
            textAlign: "center",
            minHeight: "40vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: "#fafbfc",
          }}
        >
          <PersonIcon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{ color: "#888", fontWeight: 500, mb: 1 }}
          >
            Search and select a patient to view their historical records
          </Typography>
          <Typography sx={{ color: "#aaa", fontSize: "0.9rem" }}>
            Use the search bar above to find patients by name, ID, or phone
            number
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
